import { useState, useCallback, useRef } from 'react';
import { clampToViewport } from './utils';

interface Position { x: number; y: number }
interface Size { width: number; height: number }

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | null;

interface UseDragResizeOptions {
  initialPosition: Position;
  initialSize: Size;
  minWidth: number;
  minHeight: number;
  onResizeComplete?: () => void;
}

export function useDragResize({
  initialPosition,
  initialSize,
  minWidth,
  minHeight,
  onResizeComplete,
}: UseDragResizeOptions) {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const dragStart = useRef<{ startX: number; startY: number; startPos: Position } | null>(null);
  const resizeStart = useRef<{
    startX: number; startY: number; startPos: Position; startSize: Size; edge: ResizeEdge;
  } | null>(null);

  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStart.current = { startX: e.clientX, startY: e.clientY, startPos: { ...position } };
    setIsDragging(true);
  }, [position]);

  const onDragMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.startX;
    const dy = e.clientY - dragStart.current.startY;
    const newPos = clampToViewport(
      dragStart.current.startPos.x + dx,
      dragStart.current.startPos.y + dy,
      size.width, size.height,
    );
    setPosition(newPos);
  }, [size]);

  const onDragEnd = useCallback(() => {
    dragStart.current = null;
    setIsDragging(false);
  }, []);

  const onResizeStart = useCallback((edge: ResizeEdge) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    resizeStart.current = {
      startX: e.clientX, startY: e.clientY,
      startPos: { ...position }, startSize: { ...size }, edge,
    };
    setIsResizing(true);
  }, [position, size]);

  const onResizeMove = useCallback((e: React.PointerEvent) => {
    if (!resizeStart.current) return;
    const { startX, startY, startPos, startSize, edge } = resizeStart.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newWidth = startSize.width;
    let newHeight = startSize.height;
    let newX = startPos.x;
    let newY = startPos.y;

    if (edge?.includes('e')) newWidth = Math.max(minWidth, startSize.width + dx);
    if (edge?.includes('w')) {
      newWidth = Math.max(minWidth, startSize.width - dx);
      newX = startPos.x + (startSize.width - newWidth);
    }
    if (edge?.includes('s')) newHeight = Math.max(minHeight, startSize.height + dy);
    if (edge?.includes('n')) {
      newHeight = Math.max(minHeight, startSize.height - dy);
      newY = startPos.y + (startSize.height - newHeight);
    }

    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: newX, y: newY });
  }, [minWidth, minHeight]);

  const handleResizeEnd = useCallback(() => {
    resizeStart.current = null;
    setIsResizing(false);
    onResizeComplete?.();
  }, [onResizeComplete]);

  const getResizeCursor = useCallback((edge: ResizeEdge): string => {
    const cursorMap: Record<string, string> = {
      n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
      ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize',
    };
    return edge ? cursorMap[edge] || 'default' : 'default';
  }, []);

  return {
    position, size, isDragging, isResizing, setPosition, setSize,
    dragHandleProps: {
      onPointerDown: onDragStart,
      onPointerMove: onDragMove,
      onPointerUp: onDragEnd,
      style: { cursor: isDragging ? 'grabbing' : 'grab' } as React.CSSProperties,
    },
    getResizeHandleProps: (edge: ResizeEdge) => ({
      onPointerDown: onResizeStart(edge),
      onPointerMove: onResizeMove,
      onPointerUp: handleResizeEnd,
      style: { cursor: getResizeCursor(edge) } as React.CSSProperties,
    }),
  };
}
